import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChatbotStore } from 'stores/chatbotStore';

const ChatbotSidebarPanel = dynamic(
  () =>
    import('modules/chatbot/components/ChatbotSidebarPanel').then(
      (m) => m.ChatbotSidebarPanel,
    ),
  { ssr: false },
);

const OUTER_FLEX_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  // Guarantee the sticky sidebar has at least a viewport's worth of room to fill,
  // even when page content is shorter than the viewport.
  minHeight: '100vh',
};
const CONTENT_FLEX_STYLE: React.CSSProperties = { flex: 1, minWidth: 0 };

const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 720;
// Visual gap between the panel's bottom edge and the viewport bottom so the input card
// doesn't sit flush against the browser chrome / scrollbar end.
const BOTTOM_GAP = 16;

const RESIZE_HANDLE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  width: 6,
  cursor: 'ew-resize',
  zIndex: 1,
  // Sits flush against the panel's right border; transparent so the border stays
  // the visual separator. Stays inside the panel so it's clipped when closed.
  background: 'transparent',
};

// Wraps page content to mount the chatbot sidebar on the left. When the chatbot is
// disabled (isEnabled = false), children are rendered as-is, leaving the layout untouched.
// Shared by both the shop-front default layout and the tempi layout for a consistent UX.
export const ChatbotSidebarLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    open,
    isEnabled,
    sidebarWidth,
    userSidebarWidth,
    setUserSidebarWidth,
  } = useChatbotStore();

  // Prefer user-resized width over tenant default; clamp in case persisted value
  // is out of range after viewport / config changes.
  const effectiveWidth = useMemo(() => {
    const w = userSidebarWidth ?? sidebarWidth;
    return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, w));
  }, [userSidebarWidth, sidebarWidth]);

  const isDraggingRef = useRef(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // We measure the sidebar's actual top edge in viewport
  // and write the resulting height directly to the DOM (skipping React state) so scroll
  // updates don't trigger reconciliation. rAF coalesces bursts of scroll/resize events.
  useEffect(() => {
    const el = stickyRef.current;
    const inner = innerRef.current;
    if (!el) return;

    let rafId = 0;
    const apply = () => {
      rafId = 0;
      const top = el.getBoundingClientRect().top;
      const h = Math.max(0, window.innerHeight - top - BOTTOM_GAP);
      el.style.height = `${h}px`;
      if (inner) inner.style.height = `${h}px`;
    };
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(document.documentElement);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
  }, [isEnabled]);

  const onResizeStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      isDraggingRef.current = true;

      // Lock down text selection / cursor across the page while dragging.
      const prevUserSelect = document.body.style.userSelect;
      const prevCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';

      const onMove = (ev: MouseEvent) => {
        if (!isDraggingRef.current) return;
        const next = Math.min(
          MAX_SIDEBAR_WIDTH,
          Math.max(MIN_SIDEBAR_WIDTH, ev.clientX),
        );
        setUserSidebarWidth(next);
      };

      const onUp = () => {
        isDraggingRef.current = false;
        document.body.style.userSelect = prevUserSelect;
        document.body.style.cursor = prevCursor;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [setUserSidebarWidth],
  );

  // Sticky pins the panel at `top: --chatbot-nav-height` once scrolled past the nav.
  // The calc() height is the first-paint fallback before the effect runs — the effect
  // then overwrites height imperatively on every scroll/resize.
  const sidebarStickyStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'sticky',
      top: 'var(--chatbot-nav-height, 72px)',
      alignSelf: 'flex-start',
      flexShrink: 0,
      width: open ? effectiveWidth : 0,
      height: `calc(100vh - var(--chatbot-nav-height, 72px) - ${BOTTOM_GAP}px)`,
      overflow: 'hidden',
      // Scroll-chain from inner scrollables (messages list) stops here, doesn't reach page.
      overscrollBehavior: 'contain',
      // Disable width transition while dragging so the panel tracks the cursor in real time.
      transition: isDraggingRef.current ? 'none' : 'width 0.3s ease',
      // Visual separator from main content; opaque background so shop-front's body bg
      // can't bleed through transparent regions of the inner ChatBody.
      background: '#ffffff',
      borderRight: '1px solid #e5e7eb',
      boxShadow: '2px 0 8px rgba(0, 0, 0, 0.04)',
    }),
    [open, effectiveWidth],
  );
  // Mirrors the outer height (also set imperatively by the effect). Using calc() instead
  // of `height: 100%` because `100%` on a sticky parent isn't reliably resolved across
  // every layout chain, which collapses the inner div to its content height.
  const sidebarInnerStyle = useMemo<React.CSSProperties>(
    () => ({
      width: effectiveWidth,
      height: `calc(100vh - var(--chatbot-nav-height, 72px) - ${BOTTOM_GAP}px)`,
      overflow: 'hidden',
    }),
    [effectiveWidth],
  );

  // Original flow — nothing changes compared to before the chatbot existed.
  if (!isEnabled) {
    return <>{children}</>;
  }

  return (
    <div style={OUTER_FLEX_STYLE}>
      {/* Sticky column: lives in flow next to content, pins below the nav when scrolling */}
      <div ref={stickyRef} style={sidebarStickyStyle}>
        {/* Inner has fixed width so the panel content doesn't squish when outer width: 0 (closed) */}
        <div ref={innerRef} style={sidebarInnerStyle}>
          <ChatbotSidebarPanel />
        </div>
        {open && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chatbot sidebar"
            onMouseDown={onResizeStart}
            style={RESIZE_HANDLE_BASE_STYLE}
          />
        )}
      </div>
      {/* minWidth: 0 prevents flex child from overflowing its container */}
      <div style={CONTENT_FLEX_STYLE}>{children}</div>
    </div>
  );
};
